/* ---------------------------------------------------------------------------
   One works entry, on the model — the 3D view in the works entry dialog
   (src/screens/worksEntryDialog.ts).

   THE BRIDGE AS IT STOOD THAT DAY, with the entry's own parts lit. The record
   is laid onto the model the way the playback lays it (src/data/buildSequence.ts):
   the parts poured by the end of the entry's day are drawn in their own material,
   the parts not yet poured are not drawn, and the parts this entry is about are
   drawn in the highlight whether or not they are built. Formwork standing that
   day is drawn as a see-through timber shell round its part (`formworkOn`). An entry the playback places takes the share it was dealt; one
   whose activity builds nothing (a cure, a survey) lights the parts it names.

   A CANVAS OF ITS OWN, parked on document.body over the dialog's placeholder, for
   the reason ./meshView.ts parks one: the dialog layer is re-rendered on every
   paint and a WebGL context is not something to hand a reconciler. Above the
   dialog's scrim, and hidden whenever something is opened over the dialog.

   Reads the same `.glb` as the record (src/core/geometryCache.ts), so a model
   already drawn on this device opens from IndexedDB. Writes nothing.
   --------------------------------------------------------------------------- */

import type { State } from '../core/store.ts';
import { loadGeometryBytes, parseGlb } from '../core/geometryCache.ts';
import { heroModel } from '../data/derive.ts';
import { formworkOn, partsOf, planBuild, stateOn } from '../data/buildSequence.ts';
import type { ProgressEntry } from '../data/types.ts';
import { MODEL_FOV, frameModel, modelBounds, visibleBounds } from './framing.ts';
import { measure } from './buildSim.ts';
import { FORM, formShell } from './siteWorks3d.ts';
import {
  TOOLBAR_H,
  axialDistance,
  axialFrame,
  mountViewToolbar,
  syncOrtho,
  type StandardView,
  type ViewToolbar,
} from './meshTools.ts';

/** The class of the dialog's placeholder the canvas is drawn over. */
export const ENTRY_SLOT = 'wentry__model';

/** Background behind the model, per theme — the record's. */
const BACKDROP = { light: 0xf1f2f6, dark: 0x14161c };
/** The entry's parts, per theme. */
const MARK = { light: 0xe07b0c, dark: 0xf09a2c };
/** Arm length of the orbit-point cross, in metres — the record's. */
const PIVOT_ARM = 1.2;

interface Input {
  entry: ProgressEntry;
  /**
   * What the day is read from: everything the site recorded on the structure,
   * verified or not. Unlike the playback (`playedEntries`), which shows only what
   * has been checked, this answers "what was on site that day" for an entry that
   * is itself often not yet checked — girders landed yesterday are on the bearings
   * today whether or not anybody has reviewed the entry.
   */
  entries: readonly ProgressEntry[];
}

let host: HTMLDivElement | null = null;
let showing: { bridgeId: string; modelId: string } | null = null;
let generation = 0;
let stopTracking: (() => void) | null = null;
let dispose: (() => void) | null = null;
let onResize: ((w: number, h: number) => void) | null = null;
let onInput: ((input: Input) => void) | null = null;
let onTheme: ((theme: string) => void) | null = null;
let painted: string | null = null;
let input: Input | null = null;
let covered = false;

/** The input, memoised on the record and the entry so a paint that changed neither recolours nothing. */
let inputSource: { progress: readonly ProgressEntry[]; id: string; input: Input } | null = null;

function inputFor(s: Readonly<State>, entry: ProgressEntry): Input {
  if (!inputSource || inputSource.progress !== s.progress || inputSource.id !== entry.id) {
    const recorded = s.progress.filter((e) => e.bridgeId === entry.bridgeId);
    inputSource = {
      progress: s.progress,
      id: entry.id,
      input: { entry, entries: recorded },
    };
  }
  return inputSource.input;
}

/** Follow the placeholder. The dialog is fixed and on top, so there is nothing to crop to. */
function track(): void {
  let raf = 0;
  let lastW = 0;
  let lastH = 0;
  const step = (): void => {
    raf = requestAnimationFrame(step);
    if (!host) return;
    const slot = document.querySelector<HTMLElement>(`.${ENTRY_SLOT}`);
    const box = slot?.getBoundingClientRect();
    if (!box || box.width < 2 || box.height < 2 || covered) {
      host.style.visibility = 'hidden';
      return;
    }
    host.style.visibility = 'visible';
    host.style.transform = `translate(${box.left}px, ${box.top}px)`;
    host.style.width = `${box.width}px`;
    host.style.height = `${box.height}px`;
    const w = Math.round(box.width);
    const h = Math.round(box.height);
    if (w !== lastW || h !== lastH) {
      lastW = w;
      lastH = h;
      onResize?.(w, h);
    }
  };
  raf = requestAnimationFrame(step);
  stopTracking = () => cancelAnimationFrame(raf);
}

function destroy(): void {
  generation += 1;
  stopTracking?.();
  stopTracking = null;
  dispose?.();
  dispose = null;
  onResize = null;
  onInput = null;
  onTheme = null;
  painted = null;
  host?.remove();
  host = null;
  showing = null;
}

/** A line of the legend under the canvas. */
function key(cls: string, text: string): HTMLSpanElement {
  const item = document.createElement('span');
  item.className = 'wentry__key';
  const swatch = document.createElement('i');
  swatch.className = `wentry__swatch wentry__swatch--${cls}`;
  const label = document.createElement('span');
  label.textContent = text;
  item.append(swatch, label);
  return item;
}

async function build(bridgeId: string, modelId: string, version: string | null, theme: string, mine: number): Promise<void> {
  const loading = loadGeometryBytes(bridgeId, modelId, undefined, version);
  loading.catch(() => undefined);
  const [THREE, controls, loaders] = await Promise.all([
    import('three'),
    import('camera-controls'),
    import('three/examples/jsm/loaders/GLTFLoader.js'),
  ]);
  if (mine !== generation || !host) return;
  const own = host;

  const CameraControls = controls.default;
  CameraControls.install({
    THREE: THREE as unknown as Parameters<typeof CameraControls.install>[0]['THREE'],
  });

  const slot = document.querySelector<HTMLElement>(`.${ENTRY_SLOT}`);
  const box = (slot ?? own).getBoundingClientRect();
  const width = Math.max(1, Math.round(box.width) || 640);
  /* Less the record's canvas toolbar, docked across the top — see ./meshTools.ts. */
  const height = Math.max(1, (Math.round(box.height) || 420) - TOOLBAR_H);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(width, height, false);
  renderer.domElement.className = 'wentry__canvas';
  own.prepend(renderer.domElement);

  const scene = new THREE.Scene();
  const backdrop = new THREE.Color();
  scene.background = backdrop;
  const key1 = new THREE.DirectionalLight(0xffffff, 2.1);
  key1.position.set(0.6, 1, 0.45);
  scene.add(key1);
  const fill = new THREE.DirectionalLight(0xffffff, 0.7);
  fill.position.set(-0.5, 0.35, -0.6);
  scene.add(fill);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x6b7280, 1.05));

  const lens = new THREE.PerspectiveCamera(MODEL_FOV, width / height, 0.5, 20_000);
  /* Derived from the perspective camera every frame it is drawn through, as on the record. */
  const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100_000);
  const orbit = new CameraControls(lens, renderer.domElement);
  orbit.dollyToCursor = true;
  orbit.dollySpeed = 0.5;

  /* As the playback draws its states: flat-shaded and double-sided, because the
     derived .glb carries no normals. See ./buildSim.ts. */
  const base = { side: THREE.DoubleSide, flatShading: true, metalness: 0, roughness: 0.85 } as const;
  const mark = new THREE.MeshStandardMaterial({ ...base });
  const form = new THREE.MeshStandardMaterial({
    ...base,
    color: FORM.colour,
    transparent: true,
    opacity: FORM.opacity,
    depthWrite: false,
  });
  const formEdge = new THREE.LineBasicMaterial({ color: FORM.edge });
  /* The forms, rebuilt whole on every paint: a structure has a handful standing at once. */
  const forms = new THREE.Group();
  scene.add(forms);
  const clearForms = (): void => {
    for (const child of [...forms.children]) {
      /* The shells share their part's geometry; only the edges are their own. */
      if ((child as import('three').LineSegments).isLineSegments) (child as import('three').LineSegments).geometry.dispose();
      forms.remove(child);
    }
  };

  onTheme = (next) => {
    painted = next;
    backdrop.setHex(next === 'dark' ? BACKDROP.dark : BACKDROP.light);
    const look = next === 'dark' ? MARK.dark : MARK.light;
    mark.color.setHex(look);
    mark.emissive.setHex(look);
    mark.emissiveIntensity = 0.35;
  };
  onTheme(theme);

  onResize = (w, h) => {
    const below = Math.max(1, h - TOOLBAR_H);
    renderer.setSize(w, below, false);
    lens.aspect = w / below;
    lens.updateProjectionMatrix();
  };

  /* THE TOOLBAR'S STATE, as the record keeps it (./meshView.ts). */
  let structure: import('three').Object3D | null = null;
  let projection: 'perspective' | 'orthographic' = 'perspective';
  let view: StandardView | null = null;
  let arming = false;
  let pivot: import('three').LineSegments | null = null;
  let toolbar: ViewToolbar | null = null;

  const active = (): import('three').Camera => {
    if (projection === 'orthographic') {
      syncOrtho(ortho, lens, orbit.distance);
      return ortho;
    }
    return lens;
  };

  const hidePivot = (): void => {
    if (!pivot) return;
    scene.remove(pivot);
    pivot.geometry.dispose();
    (pivot.material as import('three').Material).dispose();
    pivot = null;
  };

  const showPivot = (x: number, y: number, z: number): void => {
    hidePivot();
    const a = PIVOT_ARM;
    const geom = new THREE.BufferGeometry().setAttribute(
      'position',
      new THREE.Float32BufferAttribute([-a, 0, 0, a, 0, 0, 0, -a, 0, 0, a, 0, 0, 0, -a, 0, 0, a], 3),
    );
    const mat = new THREE.LineBasicMaterial({
      color: painted === 'dark' ? 0xf0f1f2 : 0x1a1c1e,
      depthTest: false,
      transparent: true,
      opacity: 0.9,
    });
    pivot = new THREE.LineSegments(geom, mat);
    pivot.position.set(x, y, z);
    pivot.renderOrder = 10;
    scene.add(pivot);
  };

  /* setOrbitPoint is a focal offset nothing else clears — see clearPivot in src/inspector/scene.ts. */
  const clearPivot = (): void => {
    void orbit.setFocalOffset(0, 0, 0, false);
    hidePivot();
  };

  /* Fitted to what is drawn that day, so a plan of the piers alone fills the frame. */
  const snapTo = (next: StandardView): void => {
    if (!structure) return;
    const box = visibleBounds(THREE, structure);
    if (!box) return;
    const at = axialFrame(THREE, box, next);
    const distance = axialDistance(at.sx, at.sy, lens.fov, lens.aspect);
    orbit.maxDistance = Math.max(orbit.maxDistance, distance * 1.5);
    lens.far = Math.max(lens.far, distance * 4);
    lens.updateProjectionMatrix();
    view = next;
    projection = 'orthographic';
    clearPivot();
    void orbit.setLookAt(
      at.centre.x + at.eye.x * distance,
      at.centre.y + at.eye.y * distance,
      at.centre.z + at.eye.z * distance,
      at.centre.x,
      at.centre.y,
      at.centre.z,
      true,
    );
    toolbar?.refresh();
  };

  const zoomAll = (): void => {
    if (!structure) return;
    if (view) {
      snapTo(view);
      return;
    }
    const sphere = modelBounds(THREE, structure);
    if (!sphere) return;
    clearPivot();
    frameModel(THREE, orbit, sphere, true);
    toolbar?.refresh();
  };

  /* Canvas3D.ZoomBy: a step toward the target, clamped to the dolly limits. */
  const zoomBy = (factor: number): void => {
    const next = THREE.MathUtils.clamp(orbit.distance * factor, orbit.minDistance, orbit.maxDistance);
    void orbit.dollyTo(next, true);
  };

  const setArming = (on: boolean): void => {
    arming = on;
    toolbar?.refresh();
  };

  toolbar = mountViewToolbar(own, {
    zoomIn: () => zoomBy(0.8),
    zoomOut: () => zoomBy(1.25),
    zoomAll,
    standardView: snapTo,
    activeView: () => view,
    projection: () => projection,
    setProjection: (mode) => {
      /* Leaving an axial view for perspective goes back to the three-quarter view. */
      const leaving = mode === 'perspective' && view !== null;
      projection = mode;
      view = null;
      if (leaving) zoomAll();
      toolbar?.refresh();
    },
    armOrbitPoint: setArming,
    orbitPointArmed: () => arming,
    ready: () => structure !== null,
  });

  /* A drag moves the camera off whichever of the six it was on. */
  const onControlStart = (): void => {
    if (view === null) return;
    view = null;
    toolbar?.refresh();
  };
  orbit.addEventListener('controlstart', onControlStart);

  /* Set Orbit Point is a toggle: while on, every press sets the pivot, in the
     capture phase so it runs before camera-controls caches the old one. Only drawn
     parts can be hit. */
  const onArmedDown = (ev: PointerEvent): void => {
    if (!arming || ev.button !== 0 || !structure) return;
    const rect = renderer.domElement.getBoundingClientRect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(
      new THREE.Vector2(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1),
      active(),
    );
    /* A raycast does not skip hidden meshes, and parts not yet built are hidden. */
    const hit = ray.intersectObject(structure, true).find((h) => h.object.visible);
    if (hit) {
      void orbit.setOrbitPoint(hit.point.x, hit.point.y, hit.point.z);
      showPivot(hit.point.x, hit.point.y, hit.point.z);
    }
  };
  renderer.domElement.addEventListener('pointerdown', onArmedDown, true);

  const legend = document.createElement('div');
  legend.className = 'wentry__legend';
  own.appendChild(legend);

  let raf = 0;
  const clock = new THREE.Clock();
  const draw = (): void => {
    raf = requestAnimationFrame(draw);
    orbit.update(clock.getDelta());
    renderer.render(scene, active());
  };
  draw();

  /* The model's own materials, collected before any is swapped, so the walk at the
     end disposes them and not only whichever stand-in a part last wore. */
  const materials = new Set<import('three').Material>();
  dispose = () => {
    cancelAnimationFrame(raf);
    renderer.domElement.removeEventListener('pointerdown', onArmedDown, true);
    orbit.removeEventListener('controlstart', onControlStart);
    toolbar?.destroy();
    toolbar = null;
    hidePivot();
    orbit.dispose();
    clearForms();
    form.dispose();
    formEdge.dispose();
    scene.traverse((node) => {
      const mesh = node as import('three').Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry?.dispose();
    });
    for (const m of materials) m.dispose();
    mark.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  };

  let root: import('three').Object3D;
  try {
    const geometry = await loading;
    if (mine !== generation) return;
    root = (await parseGlb(new loaders.GLTFLoader(), geometry.bytes)).scene as import('three').Object3D;
    if (mine !== generation) return;
  } catch {
    if (mine !== generation) return;
    own.classList.add('wentry--failed');
    legend.textContent = 'The 3D model could not be loaded on this device.';
    return;
  }
  scene.add(root);
  structure = root;
  toolbar?.refresh();

  const { meshes, parts } = measure(THREE, root);
  const original = new Map(meshes.map((m) => [m, m.material]));
  for (const m of original.values()) {
    if (Array.isArray(m)) m.forEach((x) => materials.add(x));
    else materials.add(m);
  }

  const sphere = modelBounds(THREE, root);
  const reach = sphere ? sphere.radius * 2 : 50;
  orbit.minDistance = Math.max(0.1, reach / 500);
  orbit.maxDistance = reach * 10;
  lens.near = Math.max(0.1, reach / 800);
  lens.far = reach * 40;
  lens.updateProjectionMatrix();
  if (sphere) frameModel(THREE, orbit, sphere, false);

  /* Closer in on the entry's parts, from the record's direction, and never so close
     that the rest of the bridge leaves the frame — a bearing beam alone is a grey
     block that could be anywhere. */
  const focus = (lit: ReadonlySet<number>, animate: boolean): void => {
    if (!sphere) return;
    /* A step to another entry reframes on its parts, off whichever of the six it was on. */
    view = null;
    clearPivot();
    toolbar?.refresh();
    const box = new THREE.Box3();
    for (const i of lit) box.expandByObject(meshes[i]!);
    if (box.isEmpty()) {
      frameModel(THREE, orbit, sphere, animate);
      return;
    }
    const around = box.getBoundingSphere(new THREE.Sphere());
    around.radius = Math.min(sphere.radius, Math.max(around.radius * 1.6, sphere.radius * 0.35));
    frameModel(THREE, orbit, around, animate);
  };
  let first = true;

  /* A shell round each part standing in forms — the playback's (./siteWorks3d.ts). */
  const showForms = (standing: ReadonlySet<number>): void => {
    clearForms();
    for (const i of standing) for (const o of formShell(THREE, meshes[i]!, form, formEdge)) forms.add(o);
  };

  const paint = (next: Input): void => {
    const plan = planBuild(parts, next.entries);
    /* The playback's share where it dealt one, else the parts the entry names. */
    const dealt = plan.claims.get(next.entry.id);
    const lit = new Set(dealt && dealt.length > 0 ? dealt : partsOf(parts, next.entry));
    const day = plan.days.indexOf(next.entry.occurredOn);
    /* Built is poured: a part with only its cage or its forms is not drawn as
       concrete, and its forms show it is under way. */
    meshes.forEach((m, i) => {
      m.material = lit.has(i) ? mark : original.get(m)!;
      m.visible = lit.has(i) || (day >= 0 && stateOn(plan, i, day) === 'done');
    });
    const standing = formworkOn(parts, next.entries, next.entry.occurredOn);
    showForms(standing);
    focus(lit, !first);
    first = false;
    legend.replaceChildren(
      ...(lit.size > 0
        ? [key('mark', 'Under construction'), key('built', 'Built by then')]
        : [key('built', 'Built by then'), key('none', 'This work is not placed on the model')]),
      ...(standing.size > 0 ? [key('form', 'Formwork in place')] : []),
    );
  };
  onInput = paint;
  if (input) paint(input);
}

/**
 * Reconcile the view with state. Called from paint() on every render.
 *
 * Up while the works entry dialog is open on a structure whose model has
 * geometry; a step to another entry recolours the scene in place and keeps the
 * camera where it was put.
 */
export function syncEntryView(s: Readonly<State>): void {
  const entry =
    s.worksEntryId === null
      ? null
      : (s.progress.find((e) => e.id === s.worksEntryId && e.bridgeId === s.selectedId) ?? null);
  const model = entry ? heroModel(s.models.models) : null;
  const wanted = entry && model?.hasGeometry ? { bridgeId: entry.bridgeId, modelId: model.id, version: model.geometryVersion } : null;

  if (!wanted || !entry) {
    if (host) destroy();
    input = null;
    return;
  }
  covered = s.progressPhotoId !== null;

  const next = inputFor(s, entry);
  if (showing && (showing.bridgeId !== wanted.bridgeId || showing.modelId !== wanted.modelId)) destroy();
  if (host) {
    if (next !== input) {
      input = next;
      onInput?.(next);
    }
    if (s.theme !== painted) onTheme?.(s.theme);
    return;
  }

  input = next;
  generation += 1;
  const mine = generation;
  host = document.createElement('div');
  host.className = 'wentry__live';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);
  showing = { bridgeId: wanted.bridgeId, modelId: wanted.modelId };
  track();
  void build(wanted.bridgeId, wanted.modelId, wanted.version, s.theme, mine).catch(() => {
    if (mine !== generation || !host) return;
    host.classList.add('wentry--failed');
  });
}
